import { useEffect, useRef } from "react";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export default function Galaxy({
  mouseRepulsion = false,
  mouseInteraction = true,
  density = 0.1,
  glowIntensity = 0.1,
  saturation = 0,
  hueShift = 70,
  twinkleIntensity = 0,
  rotationSpeed = 0.05,
  repulsionStrength = 0,
  autoCenterRepulsion = 0,
  starSpeed = 0.3,
  speed = 0.4,
  starOpacity = 1,
  starLightness = 72,
  compositeOperation = "lighter",
}) {
  const canvasRef = useRef(null);
  const pointerRef = useRef({ x: 0, y: 0, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return undefined;

    let frameId = 0;
    let width = 0;
    let height = 0;
    let stars = [];
    let startedAt = performance.now();
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);

    const createStars = () => {
      const area = width * height;
      const count = Math.floor(clamp(density, 0.08, 1.4) * area / 1250);
      const maxRadius = Math.max(width, height) * 0.78;

      stars = Array.from({ length: count }, (_, index) => {
        const arm = index % 4;
        const spread = Math.random();
        const radius = (0.12 + Math.pow(spread, 0.82) * 0.88) * maxRadius;
        const spin = radius * 0.006;
        const angle = arm * Math.PI * 0.5 + spin + (Math.random() - 0.5) * 1.55;

        return {
          angle,
          radius,
          size: 0.28 + Math.random() * 1.12,
          alpha: 0.18 + Math.random() * 0.56,
          drift: (Math.random() - 0.5) * 0.28,
          glow: 0.4 + Math.random() * 1.8,
          twinkle: Math.random() * Math.PI * 2,
          depth: 0.45 + Math.random() * 0.9,
        };
      });
    };

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * pixelRatio);
      canvas.height = Math.floor(height * pixelRatio);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      createStars();
    };

    const draw = (now) => {
      const elapsed = (now - startedAt) / 1000;
      const centerX = width / 2;
      const centerY = height / 2;
      const rotation = elapsed * rotationSpeed * speed;
      const centerPulse = Math.sin(elapsed * 0.8) * autoCenterRepulsion;

      context.clearRect(0, 0, width, height);

      const gradient = context.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        Math.max(width, height) * 0.68
      );
      gradient.addColorStop(0, `hsla(${hueShift}, ${saturation}%, 62%, 0.14)`);
      gradient.addColorStop(0.42, `hsla(${hueShift}, ${saturation}%, 38%, 0.055)`);
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);

      context.save();
      context.globalCompositeOperation = compositeOperation;

      stars.forEach((star) => {
        const localRotation = rotation * star.depth + elapsed * star.drift * starSpeed;
        let x = centerX + Math.cos(star.angle + localRotation) * (star.radius + centerPulse);
        let y = centerY + Math.sin(star.angle + localRotation) * star.radius * 0.48;

        if (mouseInteraction && pointerRef.current.active) {
          const dx = x - pointerRef.current.x;
          const dy = y - pointerRef.current.y;
          const distance = Math.max(Math.hypot(dx, dy), 1);
          const influence = clamp(1 - distance / 180, 0, 1);
          const direction = mouseRepulsion ? 1 : -0.35;
          const force = influence * repulsionStrength * direction;
          x += (dx / distance) * force;
          y += (dy / distance) * force;
        }

        const twinkle = 1 + Math.sin(elapsed * 2.6 + star.twinkle) * twinkleIntensity;
        const alpha = clamp(star.alpha * twinkle * starOpacity, 0.08, 1);
        const size = star.size * (0.8 + star.depth * 0.35);

        context.beginPath();
        context.fillStyle = `hsla(${hueShift}, ${saturation}%, ${starLightness + star.depth * 7}%, ${alpha})`;
        context.arc(x, y, size, 0, Math.PI * 2);
        context.fill();

        if (glowIntensity > 0) {
          context.beginPath();
          context.fillStyle = `hsla(${hueShift}, ${saturation}%, ${starLightness}%, ${alpha * glowIntensity * star.glow})`;
          context.arc(x, y, size * (0.36 + star.glow * 0.32), 0, Math.PI * 2);
          context.fill();
        }
      });

      context.restore();
      frameId = requestAnimationFrame(draw);
    };

    const onPointerMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      pointerRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
        active: true,
      };
    };

    const onPointerLeave = () => {
      pointerRef.current.active = false;
    };

    resize();
    startedAt = performance.now();
    frameId = requestAnimationFrame(draw);
    window.addEventListener("resize", resize);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerleave", onPointerLeave);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerleave", onPointerLeave);
    };
  }, [
    autoCenterRepulsion,
    compositeOperation,
    density,
    glowIntensity,
    hueShift,
    mouseInteraction,
    mouseRepulsion,
    repulsionStrength,
    rotationSpeed,
    saturation,
    speed,
    starLightness,
    starOpacity,
    starSpeed,
    twinkleIntensity,
  ]);

  return <canvas ref={canvasRef} className="galaxy-canvas" aria-hidden="true" />;
}
